// web/src/pages/LibraryResourcesPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import API from "../api";
import SEO from "../components/SEO";

const LibraryResources = () => {
    const [activeTab, setActiveTab] = useState("resources");
    const [searchQuery, setSearchQuery] = useState("");
    const [resources, setResources] = useState([]);
    const [journals, setJournals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [allTags, setAllTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);

    useEffect(() => {
        AOS.init({ duration: 800, once: true });
        fetchTags();
    }, []);

    // Fetch all available tags
    const fetchTags = async () => {
        try {
            const res = await API.get("/tags");
            console.log("Tags fetched:", res.data);
            setAllTags(res.data);
        } catch (err) {
            console.error("Error fetching tags:", err);
        }
    };

    // Fetch library resources with optional search
    const fetchResources = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (searchQuery) {
                params.search = searchQuery;
            }
            if (selectedTags.length > 0) {
                params.tags = selectedTags.join(',');
            }

            const res = await API.get("/resources", { params });
            const data = Array.isArray(res.data) ? res.data : res.data.resources || [];
            setResources(data);
        } catch (err) {
            console.error("Failed to fetch library resources", err);
            setError(err.message || "Failed to load library resources");
        } finally {
            setLoading(false);
        }
    }, [searchQuery, selectedTags]);

    // Fetch journals (unchanged)
    const fetchJournals = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.get("/journals");
            const data = Array.isArray(res.data) ? res.data : res.data.journals || [];
            setJournals(data);
        } catch (err) {
            console.error("Failed to fetch journals", err);
            setError(err.message || "Failed to load journals");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === "resources") {
            fetchResources();
        } else {
            fetchJournals();
        }
    }, [activeTab, fetchResources, fetchJournals]);

    // Filter items based on search query and selected tags
    const filterItems = (items) => {
        if (!items) return [];

        return items.filter(item => {
            // Filter by search query (title, category, or tags)
            const matchesSearch = !searchQuery ||
                item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.category_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (item.tags && item.tags.some(tag =>
                    tag.name?.toLowerCase().includes(searchQuery.toLowerCase())
                ));

            // Filter by selected tags
            const matchesTags = selectedTags.length === 0 ||
                (item.tags && selectedTags.every(tagId =>
                    item.tags.some(tag => tag.id === tagId)
                ));

            return matchesSearch && matchesTags;
        });
    };

    const currentItems = activeTab === "resources" ? resources : journals;
    const filteredItems = filterItems(currentItems);

    // Toggle tag selection
    const toggleTag = (tagId) => {
        setSelectedTags(prev =>
            prev.includes(tagId)
                ? prev.filter(id => id !== tagId)
                : [...prev, tagId]
        );
    };

    // Clear all selected tags
    const clearTags = () => {
        setSelectedTags([]);
    };

    const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://africaresearchuniversity.com/library-resources';
    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        'name': 'Library Resources - Africa Research University',
        'description': 'Search journals, articles and learning resources provided by Africa Research University.',
        'url': pageUrl
    };

    return (
        <section className="bg-gray-50 min-h-screen pt-1">
            <SEO title="Library Resources — Africa Research University" description="Search journals, articles and learning resources provided by Africa Research University." image="/assets/img/arulogo.png" url={pageUrl} jsonLd={jsonLd} />
            <div className="container my-16 mx-auto px-6 py-16" id="library-section">
                {/* Search and Filter Section */}
                <div className="mb-10 bg-white p-6 rounded-lg shadow-sm">
                    <div className="mb-4">
                        <input
                            type="text"
                            placeholder="Search by title, category, or tags..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Tags Filter */}
                    <div className="mb-4">
                        <h3 className="text-sm font-medium text-gray-700 mb-2">Filter by Tags:</h3>
                        <div className="flex flex-wrap gap-2">
                            {allTags.map(tag => (
                                <button
                                    key={tag.id}
                                    type="button"
                                    onClick={() => toggleTag(tag.id)}
                                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                        selectedTags.includes(tag.id)
                                            ? 'bg-blue-100 text-blue-800 border border-blue-300'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {tag.name}
                                    {selectedTags.includes(tag.id) && (
                                        <span className="ml-1.5 text-blue-600">×</span>
                                    )}
                                </button>
                            ))}
                        </div>
                        {selectedTags.length > 0 && (
                            <button
                                onClick={clearTags}
                                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                            >
                                Clear all filters
                            </button>
                        )}
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap justify-center gap-3 mb-10">
                    <button
                        onClick={() => setActiveTab("resources")}
                        className={`px-5 py-2 rounded-full font-medium transition-all border ${activeTab === "resources"
                            ? "bg-green-600 text-white border-green-600 shadow-md"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-green-50"
                            }`}
                    >
                        Library Resources
                    </button>
                    <button
                        onClick={() => setActiveTab("journals")}
                        className={`px-5 py-2 rounded-full font-medium transition-all border ${activeTab === "journals"
                            ? "bg-green-600 text-white border-green-600 shadow-md"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-green-50"
                            }`}
                    >
                        Journals
                    </button>
                </div>

                {/* Loading and Error States */}
                {loading && (
                    <div className="text-center py-10">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600 mx-auto"></div>
                        <p className="mt-2 text-gray-600">Loading {activeTab}...</p>
                    </div>
                )}

                {error && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg
                                    className="h-5 w-5 text-red-500"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Resources Grid */}
                {!loading && !error && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <AnimatePresence>
                            {filteredItems.map((item) => (
                                <motion.div
                                    key={item.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.3 }}
                                    className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
                                >
                                    <div className="p-6">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                                    {item.title}
                                                </h3>
                                                {item.category_name && (
                                                    <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium mb-3">
                                                        {item.category_name}
                                                    </span>
                                                )}
                                            </div>
                                            <a
                                                href={item.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-800"
                                            >
                                                <svg
                                                    className="h-5 w-5"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                                    />
                                                </svg>
                                            </a>
                                        </div>

                                        {item.tags && item.tags.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-1">
                                                {item.tags.map((tag, index) => (
                                                    <span
                                                        key={index}
                                                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                                    >
                                                        {tag.name}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        <p className="mt-3 text-gray-600 text-sm">
                                            {item.description?.substring(0, 150)}
                                            {item.description?.length > 150 ? "..." : ""}
                                        </p>

                                        <div className="mt-4 flex justify-between items-center">
                                            <a
                                                href={item.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                            >
                                                Open Resource
                                            </a>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                )}

                {/* Empty State */}
                {!loading && !error && filteredItems.length === 0 && (
                    <div className="text-center py-16">
                        <svg
                            className="mx-auto h-12 w-12 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1}
                                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        <h3 className="mt-2 text-lg font-medium text-gray-900">
                            No {activeTab} found
                        </h3>
                        <p className="mt-1 text-gray-500">
                            {searchQuery || selectedTags.length > 0
                                ? "Try adjusting your search or filter criteria."
                                : `No ${activeTab} are currently available.`}
                        </p>
                        {selectedTags.length > 0 && (
                            <button
                                onClick={clearTags}
                                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                Clear all filters
                            </button>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};

export default LibraryResources;